import { LIVE_BREAK_DEFAULT_MIN_BOX_PRICE_CENTS } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Field, FieldContent, FieldDescription, FieldLabel } from "@loot-os/ui/components/field";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@loot-os/ui/components/sheet";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";

import { useCurrency } from "../lib/currency";
import {
  houseLiveBreakInput,
  type LiveBreakDraft,
  liveBreakDraftIssues,
  newHouseLiveBreakDraft,
  type RefusedLiveBreakBoxes,
  refusedLiveBreakBoxesOf,
} from "../lib/liveBreakDraft";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { LiveBreakSettingsFields } from "./LiveBreakSettingsFields";

const NO_REFUSED_BOXES: RefusedLiveBreakBoxes = new Map();

/**
 * New house live break (`liveBreaks.createHouse`, admins): a live break the store runs under its
 * own name, always in Auto Mode, at box prices and earning no commission. It opens the new live
 * break's page once created. Platform admins also see the dev profile switch ("idle_end": it ends
 * itself once idle and respawns, which is how a dev environment opts in; production refuses it).
 * A box the create was refused for is marked in the list.
 */
export function CreateHouseLiveBreakSheet() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const currency = useCurrency();
  const { isPlatformAdmin } = useTenantRole();
  const settings = useStorefrontConfigQuery().query.data?.config.liveBreakSettings;
  const minBoxPriceCents =
    settings?.defaultMinBoxPriceCents ?? LIVE_BREAK_DEFAULT_MIN_BOX_PRICE_CENTS;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<LiveBreakDraft | null>(null);
  // Errors show once the first save is tried, so an empty form does not open red.
  const [tried, setTried] = useState(false);
  const [refusedBoxes, setRefusedBoxes] = useState<RefusedLiveBreakBoxes>(NO_REFUSED_BOXES);
  const reset = () => {
    setDraft(null);
    setTried(false);
    setRefusedBoxes(NO_REFUSED_BOXES);
  };
  const current = draft ?? newHouseLiveBreakDraft(minBoxPriceCents, currency);
  const now = new Date();
  const issues = liveBreakDraftIssues(current, { currency, isHouse: true, maxRateBps: 0, now });
  const input = houseLiveBreakInput(current, { currency, now });

  const create = useMutation(
    trpc.liveBreaks.createHouse.mutationOptions({
      onSuccess: ({ liveBreak }) => {
        toast.success(`${liveBreak.title} is open.`);
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.pathKey() });
        setOpen(false);
        reset();
        void navigate({
          to: "/tenants/$tenantId/storefront/live-breaks/list/$liveBreakId",
          params: { tenantId, liveBreakId: liveBreak.id },
        });
      },
      // The toast names the refusal; the list marks which boxes it was for.
      onError: (error) => setRefusedBoxes(refusedLiveBreakBoxesOf(error)),
    }),
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <SheetTrigger render={<Button type="button" size="sm" />}>
        <Plus />
        New house live break
      </SheetTrigger>
      <SheetContent className="gap-0 overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-2xl">
        <form
          className="flex min-h-full flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            setTried(true);
            if (input) create.mutate(input);
          }}
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle className="text-base">New house live break</SheetTitle>
            <SheetDescription>
              Run by the store under its own name: always in Auto Mode, sold at box prices, earning
              no commission. Every house live break shares one set of Blocks.
            </SheetDescription>
          </SheetHeader>
          <div className="grid flex-1 gap-6 p-4">
            <LiveBreakSettingsFields
              idPrefix="house-new"
              mode="create"
              isHouse
              draft={current}
              issues={tried ? issues : {}}
              maxRateBps={0}
              disabled={create.isPending}
              refusedBoxes={refusedBoxes}
              onChange={(patch) => setDraft({ ...current, ...patch })}
            />
            {isPlatformAdmin && (
              <Field orientation="horizontal">
                <Switch
                  id="house-new-idle-end"
                  checked={current.houseProfile === "idle_end"}
                  disabled={create.isPending}
                  onCheckedChange={(checked) =>
                    setDraft({ ...current, houseProfile: checked ? "idle_end" : null })
                  }
                />
                <FieldContent>
                  <FieldLabel htmlFor="house-new-idle-end">Ends itself when idle (dev)</FieldLabel>
                  <FieldDescription>
                    Ends itself once its stage sits idle and empty after an Opening, then starts
                    again with the same boxes once it leaves the directory, so the environment
                    always has one to test against. Dev and personal stages only; production refuses
                    it.
                  </FieldDescription>
                </FieldContent>
              </Field>
            )}
          </div>
          <SheetFooter className="border-t sm:flex-row sm:justify-end">
            <Button type="submit" disabled={create.isPending || (tried && input === null)}>
              {create.isPending ? "Creating…" : "Create live break"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
